import {
  MAX_CITY_LENGTH,
  MAX_EMAIL_LENGTH,
  MAX_NAME_LENGTH,
  MAX_PHONE_LENGTH,
  MAX_ZIP_LENGTH,
} from '@/validation';
import React from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { CountryRegionField, StateProvinceField } from '@/components/address-fields';
import { Stack } from '@/components/layout';
import { FormInput, Text } from '@/components/ui';
import type { FormInputProps } from '@/components/ui/form/form-input';
import type { FormSelectInputProps } from '@/components/ui/form/form-select-input';
import { STRINGS } from '@/constants/strings';
import { type OnboardingProfileForm } from '@/features/onboarding/schemas/onboarding-profile-schema';
import { createStyles, useScaling } from '@/hooks';
import { useAvatarPicker } from '@/hooks/use-avatar-picker';
import { colors, guardianColors } from '@/theme/colors';

import { useGuardianProfileQuery } from '@/features/guardian/queries';
import { useSessionStore } from '@/stores/session-store';
import { useShallow } from 'zustand/shallow';
import { OnboardingAvatar } from './onboarding-avatar';
import { OnboardingNotice } from './onboarding-notice';

const strings = STRINGS.onboarding.step1;

/**
 * Onboarding step 1 — the guardian profile form (avatar, name,
 * contact + address). Built from the shared `FormInput` / `FormSelectInput`
 * primitives. Reads the react-hook-form context provided by the screen (validated
 * against `onboardingProfileSchema`) so the wizard's "Continue" can submit it.
 */
export function OnboardingStepProfile() {
  const { scale } = useScaling();
  const styles = useStyles();

  // Figma input styling, applied via the FormInput exposed props (no component edits):
  // label not uppercased + Open Sans 12 SemiBold, 4px label gap, light #e2e8f0 border,
  // 8px radius, 48px height, 12px horizontal padding, #252525 text.
  const sharedFieldProps = {
    uppercaseLabel: false,
    labelVariant: 'Font Guardian App/Subtitle/Subtitle 12 Semibold',
    inputVariant: 'Font Guardian App/Subtitle/Subtitle 14 Regular',
    labelGap: scale(4),
    labelStyle: styles.fieldLabel,
    inputStyle: styles.fieldText,
    contentStyle: styles.fieldContent,
  } satisfies Partial<FormInputProps>;

  // Phone variant adds the vertical divider between the flag and the number.
  const phoneFieldProps = {
    ...sharedFieldProps,
    phoneDivider: true,
    phoneDividerStyle: styles.phoneDivider,
    flagProps: { styleWrapper: styles.flagWrapper },
  } satisfies Partial<FormInputProps>;

  // Same Figma styling for the select trigger (reuses the field token styles).
  const selectFieldProps = {
    uppercaseLabel: false,
    labelVariant: 'Font Guardian App/Subtitle/Subtitle 12 Semibold',
    labelGap: scale(4),
    labelStyle: styles.fieldLabel,
    triggerVariant: 'Font Guardian App/Subtitle/Subtitle 14 Regular',
    triggerStyle: styles.fieldContent,
  } satisfies Partial<FormSelectInputProps>;

  // State/Province may render as a free-text field, so it also needs the input styling.
  const stateFieldProps = {
    ...selectFieldProps,
    inputVariant: 'Font Guardian App/Subtitle/Subtitle 14 Regular',
    inputStyle: styles.fieldText,
    contentStyle: styles.fieldContent,
  } as const;

  const { control, setValue } = useFormContext<OnboardingProfileForm>();
  const user = useSessionStore(useShallow(s => s.user));
  const { data: guardian } = useGuardianProfileQuery(user?.uid);
  const pickAvatar = useAvatarPicker();

  // Country holds the ISO code on the form (mapped to the country name when sent to the API).
  // It starts unselected and drives the State/Province options.
  const selectedCountry = useWatch({ control, name: 'country' });

  // Pick → crop/compress → validate size; the picker hook handles permissions
  // and unsupported-format alerts, the schema flags files over 10MB.
  const handlePickAvatar = () => {
    pickAvatar(value => {
      setValue('avatar', value, { shouldValidate: true, shouldDirty: true, shouldTouch: true });
    });
  };

  return (
    <Stack>
      {/* Heading */}
      <Text variant="Font Guardian App/Heading/Heading H6 18px" style={styles.title}>
        {strings.title.replace('{name}', guardian?.firstname ?? '')}
      </Text>
      <Text variant="Font Guardian App/Subtitle/Subtitle 14 Regular" style={styles.subtitle}>
        {strings.subtitle}
      </Text>

      {/* Avatar */}
      <Stack align="center" marginTop={scale(24)}>
        <Controller
          name="avatar"
          control={control}
          render={({ field: { value }, fieldState: { error } }) => {
            const fileSizeMessage = (error as { fileSize?: { message?: string } } | undefined)
              ?.fileSize?.message;
            return (
              <OnboardingAvatar
                source={value?.uri}
                onPress={handlePickAvatar}
                hasError={Boolean(error)}
                errorMessage={fileSizeMessage}
              />
            );
          }}
        />
      </Stack>

      {/* Name row */}
      <Stack direction="row" gap={scale(12)} marginTop={scale(24)}>
        <Stack flex={1}>
          <Controller
            name="firstName"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <FormInput
                {...sharedFieldProps}
                label={strings.labelFirstName}
                required
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                status={error?.message ? 'error' : 'default'}
                errorMessage={error?.message}
                placeholder={strings.placeholderFirstName}
                autoCapitalize="words"
                maxLength={MAX_NAME_LENGTH}
              />
            )}
          />
        </Stack>
        <Stack flex={1}>
          <Controller
            name="lastName"
            control={control}
            render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
              <FormInput
                {...sharedFieldProps}
                label={strings.labelLastName}
                required
                value={value}
                onChangeText={onChange}
                onBlur={onBlur}
                status={error?.message ? 'error' : 'default'}
                errorMessage={error?.message}
                placeholder={strings.placeholderLastName}
                autoCapitalize="words"
                maxLength={MAX_NAME_LENGTH}
              />
            )}
          />
        </Stack>
      </Stack>

      {/* Other information */}
      <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.sectionHeader}>
        {strings.sectionOther}
      </Text>

      <Stack gap={scale(12)}>
        <Controller
          name="phoneNumber"
          control={control}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <FormInput
              {...phoneFieldProps}
              label={strings.labelPhone}
              required
              phoneNumber
              phoneDefaultCountry="US"
              keyboardType="phone-pad"
              maxLength={MAX_PHONE_LENGTH}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              onCountryCodeChange={code => setValue('countryCode', code ?? null)}
              status={error?.message ? 'error' : 'default'}
              errorMessage={error?.message}
              flagProps={{
                styleWrapper: {
                  marginLeft: scale(0),
                  marginRight: scale(0),
                },
              }}
              placeholder={''}
            />
          )}
        />
        <Controller
          name="altPhoneNumber"
          control={control}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <FormInput
              {...phoneFieldProps}
              label={strings.labelAltPhone}
              phoneNumber
              phoneDefaultCountry="US"
              keyboardType="phone-pad"
              maxLength={MAX_PHONE_LENGTH}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              onCountryCodeChange={code => setValue('altCountryCode', code ?? null)}
              status={error?.message ? 'error' : 'default'}
              errorMessage={error?.message}
              placeholder={''}
              flagProps={{
                styleWrapper: {
                  marginLeft: scale(0),
                  marginRight: scale(0),
                },
              }}
            />
          )}
        />
        <Controller
          name="email"
          control={control}
          render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
            <FormInput
              {...sharedFieldProps}
              label={strings.labelEmail}
              required
              value={value}
              disabled
              onChangeText={onChange}
              onBlur={onBlur}
              status={error?.message ? 'error' : 'default'}
              errorMessage={error?.message}
              placeholder={strings.placeholderEmail}
              keyboardType="email-address"
              autoCapitalize="none"
              disableStyle={{
                backgroundColor: guardianColors[1],
              }}
              autoComplete="email"
              trim
              maxLength={MAX_EMAIL_LENGTH}
            />
          )}
        />
        {/* Country + State/Province row */}
        <Stack direction="row" gap={scale(12)} align="flex-start">
          <Stack flex={1}>
            <Controller
              name="country"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <CountryRegionField
                  {...selectFieldProps}
                  label={strings.labelCountry}
                  required={false}
                  value={value}
                  onValueChange={next => {
                    onChange(next);
                    // Clear the state — the previous selection may not exist in the new country.
                    setValue('state', null, { shouldDirty: true });
                  }}
                  status={error?.message ? 'error' : 'default'}
                  errorMessage={error?.message}
                  placeholder={strings.placeholderCountry}
                />
              )}
            />
          </Stack>
          <Stack flex={1}>
            <Controller
              name="state"
              control={control}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <StateProvinceField
                  {...stateFieldProps}
                  label={strings.labelState}
                  required={false}
                  country={selectedCountry}
                  value={value}
                  onValueChange={onChange}
                  status={error?.message ? 'error' : 'default'}
                  errorMessage={error?.message}
                />
              )}
            />
          </Stack>
        </Stack>

        {/* City + Zip row */}
        <Stack direction="row" gap={scale(12)} align="flex-start">
          <Stack flex={1}>
            <Controller
              name="city"
              control={control}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <FormInput
                  {...sharedFieldProps}
                  label={strings.labelCity}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  status={error?.message ? 'error' : 'default'}
                  errorMessage={error?.message}
                  placeholder={strings.placeholderCity}
                  autoCapitalize="words"
                  maxLength={MAX_CITY_LENGTH}
                />
              )}
            />
          </Stack>
          <Stack flex={1}>
            <Controller
              name="zipCode"
              control={control}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <FormInput
                  {...sharedFieldProps}
                  label={strings.labelZip}
                  value={value}
                  onChangeText={zipCode => onChange(zipCode.replace(/[^0-9]/g, ''))}
                  onBlur={onBlur}
                  status={error?.message ? 'error' : 'default'}
                  errorMessage={error?.message}
                  placeholder={strings.placeholderZip}
                  keyboardType="numeric"
                  maxLength={MAX_ZIP_LENGTH}
                />
              )}
            />
          </Stack>
        </Stack>
      </Stack>

      {/* Match notice */}
      <Stack marginTop={scale(16)}>
        <OnboardingNotice>{strings.notice}</OnboardingNotice>
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  title: {
    color: colors['10'],
  },
  subtitle: {
    marginTop: scale(8),
    color: colors['10'],
  },
  roleLabel: {
    color: colors['10'],
  },
  sectionHeader: {
    marginTop: scale(16),
    marginBottom: scale(8),
    color: guardianColors['10'],
  },
  fieldLabel: {
    color: colors['10'],
  },
  fieldText: {
    color: colors['10'],
    marginLeft: 0,
  },
  fieldContent: {
    borderColor: colors['3'],
    borderRadius: scale(8),
    height: scale(48),
    paddingLeft: scale(12),
    paddingRight: scale(12),
  },
  phoneDivider: {
    marginHorizontal: scale(8),
  },
  flagWrapper: {
    marginLeft: 0,
    width: scale(28),
  },
}));

export default OnboardingStepProfile;
